import { filterStoreRepo } from 'components/Filters/FilterStore'
import FilterModal, {
  FilterGroup,
} from 'components/Filters/components/FilterModal'
import { DateRangeFilter } from 'components/Filters/fields/DateRangeFilter'
import {
  SelectFilter,
  SelectFilterOption,
} from 'components/Filters/fields/SelectFilter'
import { SingleSelectFilter } from 'components/Filters/fields/SingleSelectFilter'
import { FilterIdentifier } from 'models/saved_view'
import apiService from 'services/api'
import { NOTES_COLUMNS } from './notesColumnInfo'
import { useMemo } from 'react'
import { createFilterColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { ALL_COUNTRIES } from 'utils/countries'

interface NotesFiltersetProps {
  filterIdentifier: FilterIdentifier
  showDoorFilters?: boolean
  contactCompanyId?: number
}

export default function NotesFilterset({
  filterIdentifier,
  showDoorFilters = false,
  contactCompanyId,
}: NotesFiltersetProps) {
  const api = apiService()
  const { data: rawUserOptions } = api.useGetUserOptions({
    onlyActive: true,
    withNoneOption: true,
  })

  // Get the city options for door proxies associated with this contact company
  const { data: cityOptions } =
    contactCompanyId && showDoorFilters
      ? api.useGetDoorProxyCities({ contactCompanyId })
      : { data: [] }

  // Get the state options for door proxies associated with this contact company
  const { data: stateOptions } =
    contactCompanyId && showDoorFilters
      ? api.useGetDoorProxyStates({ contactCompanyId })
      : { data: [] }

  // Get product tags when showing door filters
  const { data: rawProductTagsOptions } = showDoorFilters
    ? api.useGetTagsOptions(false)
    : { data: undefined }

  const userOptions: SelectFilterOption[] | undefined = useMemo(() => {
    return rawUserOptions?.map((option) => ({
      ...option,
      value: String(option.value),
    }))
  }, [rawUserOptions])

  const productTagsOptions: SelectFilterOption[] | undefined = useMemo(() => {
    return rawProductTagsOptions?.map((option) => ({
      ...option,
      value: String(option.value),
    }))
  }, [rawProductTagsOptions])

  const notesFilterStore = filterStoreRepo.getStore(filterIdentifier)
  const notesFilterHelper = createFilterColumnHelper({
    columns: NOTES_COLUMNS,
  })

  return (
    <FilterModal store={notesFilterStore} identifier={filterIdentifier}>
      <FilterGroup title="Date Filters">
        <DateRangeFilter
          {...notesFilterHelper.retrieveProps('created')}
          filterStore={notesFilterStore}
        />
        <DateRangeFilter
          {...notesFilterHelper.retrieveProps('modified')}
          filterStore={notesFilterStore}
        />
      </FilterGroup>
      <FilterGroup title="Note Attributes">
        <SelectFilter
          {...notesFilterHelper.retrieveProps('user')}
          options={userOptions || []}
          filterStore={notesFilterStore}
        />
        <SingleSelectFilter
          title={NOTES_COLUMNS.has_attachment.header}
          filterKey="has_attachment"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          filterStore={notesFilterStore}
          hideSearch
        />
      </FilterGroup>
      {showDoorFilters && (
        <FilterGroup title="Door Filters">
          <SelectFilter
            title="Country"
            filterKey="door_country"
            options={ALL_COUNTRIES}
            filterStore={notesFilterStore}
          />
          <SelectFilter
            title="City"
            filterKey="door_city"
            options={cityOptions || []}
            filterStore={notesFilterStore}
          />
          <SelectFilter
            title="State"
            filterKey="door_state"
            options={stateOptions || []}
            filterStore={notesFilterStore}
          />
          <SelectFilter
            title="Door Tags"
            filterKey="door_tags"
            options={productTagsOptions || []}
            filterStore={notesFilterStore}
          />
        </FilterGroup>
      )}
    </FilterModal>
  )
}
